import React, { useContext, useEffect, useState } from "react";
import { ContentWrapper, DetailsBox, DetailsContent, DetailsRow, DetailsSectionHeader, ItemWrapper } from "../styles";
import Loader from "../../../../components/common/Loader";
import { formatCurrencyAmount } from "../../../../utils/currency";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { clearUserData, setStep } from "../../redux/formDataSlice";
import dayjs from "dayjs";
import { formatValue } from "../../utils";
import WalletOTP from "../../components/SendFunds/components/WalletOTP";
import { sendFundsAction } from "../../redux/slice";
import EventFactory from "../../../../utils/eventFactory";
import { sendOTP } from "../../../../api/usersAPI";
import { capitalizeCurrencyCode } from "../../../../utils/helpers";
import { Button } from "../../../../components/common/Button";
import { useNavigate, useParams } from "react-router-dom";
import { SuccessAlertContext } from "../../../../context/SuccessAlertContext";
import { NetworkErrorAlertContext } from "../../../../context/NetworkErrorAlert";
import { getAllWallets } from "../../api";
import { merchantBusinessKeys } from "../../../Settings/settingsSlice";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/react";

type ReviewTypes = {
    handlePrevious: () => void;
};

type ReviewState = {
    publicKey: string;
    taxRefId: string;
    loading: boolean;
    verifyOTP: boolean;
};

type FormData = {
    amount?: number;
    country?: string;
    currency?: string;
    bankCode?: string;
    bankName?: string;
    accountNumber?: string;
    accountName?: string;
    requestId?: string;
    description?: string;
    type?: string;
    recipientAddress?: string;
    mobileNumber?: string;
    serviceCode?: string;
    recipientEmail?: string;
}

const Review = ({ handlePrevious }: ReviewTypes) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { currencyCode } = useParams();
    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);
    const { businessId,
        userObj: { accountUser },
    } = useAppSelector((state) => state.users);
    const { loading, wallets } = useAppSelector((state) => state.wallets);
    const wallet = wallets?.find((wallet) => wallet.currency === "NGN");
    

    const eventFactory = EventFactory.getInstance();

    const [reviewData, setReviewData] = useState<ReviewState>();

    const successAlertContext = useContext(SuccessAlertContext);

    const { onShowAlert: onSuccess } = successAlertContext || {};
    const { onShowAlert } = useContext(NetworkErrorAlertContext);

    const currentDate = dayjs().format("YYYY-MM-DD");

    const updateReviewData = (fields: Partial<ReviewState>) =>
        setReviewData((prev) => ({ ...prev, ...fields }));

    useEffect(() => {
        if (businessId) {
            dispatch(merchantBusinessKeys(businessId))?.then(({ payload }) => {
                updateReviewData({
                    publicKey: payload?.data?.publicKey ?? "",
                });
            });
        }
    }, [businessId, dispatch]);

    useEffect(() => {
        updateReviewData({ taxRefId: `tranf-${uuidv4()}` });
    }, []);

    const trackPayout = (status: "success" | "failed", errorMessage?: string) => {
        eventFactory.trackPayout(
            getSavedData?.amount,
            capitalizeCurrencyCode(currencyCode),
            "bank_transfer",
            status,
            errorMessage,
        );
    };

    const handleSendOTP = async (user: { email: string }) => {
        updateReviewData({ loading: true });
        try {
            await sendOTP(user);
            eventFactory.captureEvent("payout_otp", {
                status: "success",
                currency: capitalizeCurrencyCode(currencyCode),
            });
            updateReviewData({ verifyOTP: true, loading: false });
        } catch (err: any) {
            updateReviewData({ loading: false });
            let message = "";
            const errorObj = err?.response?.data;
            if (typeof errorObj?.error === "string") message = errorObj.error;
            else if (typeof errorObj?.message === "string")
                message = errorObj.message;
            else if (typeof errorObj === "string") message = errorObj;
            else message = "Failed to send OTP";
            eventFactory.captureEvent("payout_otp", {
                status: "failure",
                failure_reason: message,
                currency: capitalizeCurrencyCode(currencyCode) || "",
            });
        }
    };

    const handlePayout = async () => {
        const payload = {
            sourceCurrency: "NGN",
            destinationCurrency: "NGN",
            amount: Number(getSavedData?.amount),
            requestId: reviewData.taxRefId,
            accountHolderName: getSavedData?.accountName,
            bankCode: getSavedData?.bankCode,
            bankName: getSavedData?.selectedBank?.label,
            accountNumber: getSavedData?.accountNumber,
            type: "corporate",
            email: accountUser?.email,
            fee: getSavedData?.fee,
            narration: getSavedData?.paymentDescription,
            wallet: wallet?.id,
        };

        const actionResult = dispatch(
            sendFundsAction({ payload: payload}),
        );

        actionResult.then((result) => {
            if (sendFundsAction.fulfilled.match(result)) {
                getAllWallets({ businessId });
                onSuccess("Payout successfully sent");
                trackPayout("success");
                dispatch(clearUserData());
                navigate(`/wallets/`);
            } else if (sendFundsAction.rejected.match(result)) {
                const errorMessage =
                    (result.payload as string) ?? "Failed to send from wallet.";
                onShowAlert("", errorMessage);
                trackPayout("failed", errorMessage);
                Sentry.captureException(sendFundsAction);
            }
        });
    };

    return (
        <ContentWrapper>
            <Loader isLoading={reviewData?.loading || loading} />
            {reviewData?.verifyOTP && (
                <WalletOTP
                    setVerifyOTP={(v) => updateReviewData({ verifyOTP: v })}
                    makePayment={handlePayout}
                />
            )}

            <DetailsBox>
                <div>
                    <DetailsSectionHeader>
                        <h1>Transaction details</h1>
                        <span onClick={() => dispatch(setStep(0))}>Edit</span>
                    </DetailsSectionHeader>
                    <DetailsContent>
                        <DetailsRow>
                            <p>Amount sent</p>
                            <span>
                                {formatCurrencyAmount("NGN", getSavedData?.amount)}
                            </span>
                        </DetailsRow>
                        <DetailsRow>
                            <p>Processing Fee</p>
                            <span>{formatCurrencyAmount("NGN", getSavedData?.fees)}</span>
                        </DetailsRow>
                        {getSavedData?.description && (
                            <DetailsRow>
                                <p>Description</p>
                                <span>{getSavedData?.description}</span>
                            </DetailsRow>
                        )}

                        <DetailsRow>
                            <p>Total amount charged</p>
                            <span>{formatCurrencyAmount("NGN", getSavedData?.totalAmount)}</span>
                        </DetailsRow>
                        <DetailsRow>
                            <p>Date</p>
                            <span>{currentDate}</span>
                        </DetailsRow>
                    </DetailsContent>
                </div>

                <div>
                    <DetailsSectionHeader>
                        <h1>Beneficiary details</h1>
                        <span onClick={() => dispatch(setStep(1))}>Edit</span>
                    </DetailsSectionHeader>
                    <DetailsContent>
                        <DetailsRow>
                            <p>Beneficiary name</p>
                            <span>{formatValue(getSavedData?.accountName)}</span>
                        </DetailsRow>

                        <DetailsRow>
                            <p>
                                Account number
                            </p>
                            <span>{getSavedData?.accountNumber}</span>
                        </DetailsRow>


                        <DetailsRow>
                            <p>
                                Bank name
                            </p>
                            <span>{getSavedData?.selectedBank?.label}</span>
                        </DetailsRow>
                    </DetailsContent>
                </div>
            </DetailsBox>

            <ItemWrapper>
                <Button
                    onClick={handlePrevious}
                    label="Prev"
                    height="48px"
                    theme="alternate"
                    width="203px"
                />

                <Button
                    label="Continue"
                    height="48px"
                    onClick={() => handleSendOTP(accountUser?.email)}
                />
            </ItemWrapper>

        </ContentWrapper>
    )
}

export default Review